import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/joy";
import { alpha } from "@mui/material";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { useTour } from "@reactour/tour";
import Id from "atoms/Id/Id";
import { Assignment } from "entities/assignment";
import MercuryMercuryJob_Entity from "entities/mercuryJob/mercuryJob";
import { Mission_Entity } from "entities/mission";
import { Musician, Musician_Entity } from "entities/musician";
import { RhapsodyChair_Entity } from "entities/rhapsodyChair";
import { Stage } from "entities/stage";
import { ChairAssignMusician } from "features/chair/v2/chairAssignMusician";
import { useAskQuestion } from "features/context/AskQuestion/AskQuestion";
import MusicianAvatar from "features/musicians/MusicianAvatar";
import SeasonNotify from "features/seasons/SeasonNotify";
import { RouterConfig } from "hooks/AppRouter/AppRouter";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  setBackToSeason,
  setFormOpen,
  setSelectedProjectID,
} from "reducers/rhapsody";
import {
  layoutDebugSelector,
  setAssignmentIDForLookup,
} from "reducers/v2/missionControl";
import {
  seasonUtilsSelector,
  selectMusicians,
  selectedMusiciansSelector,
  unselectMusicians,
} from "reducers/v2/seasonControl";
import { rhapsodyApi } from "redux/api";
import { useCompanySettings } from "redux/company/companyHooks";
import { useUpdateMissionMutation } from "redux/mission/missionEndpoints";
import {
  useLazyGetChairsQuery,
  useUpdateBatchChairsMutation,
} from "redux/rhapsodyChair/rhapsodyChairEndpoints";

/**
 *
 * @returns {ReactElement} SeasonMusicians page
 */
export function Missions() {
  const {
    missions,
    musicians,
    musiciansMap,
    missionsMap,
    seasonMercury,
    assignments,
    projects,
    projectsMap,
    activeMusicians,
    workSessions,
  } = useSelector(seasonUtilsSelector);
  const settings = useCompanySettings();
  const [updateMission] = useUpdateMissionMutation();
  const [replace, setReplace] = useState({ from: undefined, to: undefined });
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  let interval;
  const datagridApi = useGridApiRef();

  useEffect(() => {
    if (musiciansMap[replace.to]) interval = setInterval(showMusician, 200);
  }, [musiciansMap]);

  const rows =
    activeMusicians?.reduce((a, v) => {
      const item = { ...v };
      projects.forEach((p) => {
        item[p.id] = true;
      });
      a.push(item);
      return a;
    }, []) ?? [];

  const showMusician = () => {
    datagridApi.current.scrollToIndexes({
      rowIndex: rows.findIndex((m) => m.id === replace.to),
      colIndex: 0,
    });
    const elemt = window.document.getElementsByClassName(
      `musician_${replace.to}`
    );

    if (elemt.length) {
      setSteps([
        {
          selector: `.musician_${replace.to}`,
          content: (
            <Box>
              <Typography level="h6" sx={{ color: "inherit" }}>
                Success
              </Typography>
              <Typography level="body2" sx={{ color: "inherit" }}>
                <b>
                  <MusicianAvatar
                    size={20}
                    musician={musiciansMap[replace.from]}
                  />{" "}
                  {musiciansMap[replace.from].fullName()}
                </b>{" "}
                has been replaced by{" "}
                <b>
                  <MusicianAvatar
                    size={20}
                    musician={musiciansMap[replace.to]}
                  />{" "}
                  {musiciansMap[replace.to].fullName()}
                </b>
              </Typography>
            </Box>
          ),
        },
      ]);
      setIsOpen(false);
      setCurrentStep(0);
      setIsOpen(true);
      setReplace({ from: undefined, to: undefined });
      clearInterval(interval);
    }
  };

  const onUpdate = async (_new: Musician & { memo: string }) => {
    return new Promise<Musician_Entity>((resolve) => {
      const mission = missions.find((m) => m.musicianID === _new.id);
      const body = { ...missionsMap[mission.id], memo: _new.memo };
      updateMission({
        id: mission.id,
        body,
      });
      setTimeout(() => {
        resolve({ ..._new });
      }, 50);
    });
  };

  const columns: any = [
    {
      field: "musicianID",
      sortable: false,
      renderHeader: () => <MusicianHeader />,
      width: 200,
      headerName: "Musician",
      valueGetter: (p) => musiciansMap[p.row.id]?.lastName ?? "",
      renderCell: (p) => (
        <MusicianCell
          musician={musiciansMap[p.row.id]}
          mission={missions.find((m) => m.musicianID === p.row.id)}
        />
      ),
    },
  ];

  projects.map((project) => {
    columns.push({
      field: `${project.id}`,
      headerName: project.name,
      sortable: false,
      width: 150,
      align: "center",
      type: "boolean",
      renderCell: (p) => {
        const mission = missions.find((m) => m.musicianID === p.row.id);
        const job = seasonMercury?.jobs.find(
          (j) => j.uuid === mission?.mercuryJobID
        );
        const assignment = assignments.find(
          (a) => a.musicianID === p.row.id && `${a.projectID}` === p.field
        );
        const projectWorkSessions = workSessions.filter(
          (w) => w.projectID === parseInt(p.field)
        );

        if (projectWorkSessions.length === 0) return <Box />;
        return <MusicianProject job={job} assignment={assignment} />;
      },
      renderHeader: (p) => <ProjectHeader projectID={p.field} />,
    });
  });

  if (settings.missions || missions?.length) {
    columns.push({
      field: "memo",
      sortable: false,
      headerName: "Mission Memo",
      editable: true,
      minWidth: 350,
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography level="body2">Mission Memo</Typography>
        </Box>
      ),
      renderCell: (p) => {
        const mission = missions.find((m) => m.musicianID === p.row.id);
        const job = seasonMercury?.jobs.find(
          (j) => j.uuid === mission?.mercuryJobID
        );

        if (!job) return "";
        return (
          <Typography
            level="body4"
            sx={{
              opacity: mission?.memo ? 1 : 0.3,
              color: "black",
              fontWeight: mission?.memo ? 600 : undefined,
            }}
          >
            {mission?.memo ? mission.memo : `Any info to add for this mission?`}
          </Typography>
        );
      },
      type: "string",
    });
  }

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: "auto",
        height: 0,
        position: "relative",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {musicians != undefined ? (
        <DataGridPremium
          isCellEditable={(p) => {
            if (p.field !== "memo") return false;
            const mission = missions.find((m) => m.musicianID === p.row.id);
            const job = seasonMercury.jobs.find(
              (j) => j.uuid === mission?.mercuryJobID
            );
            return p.field === "memo" && job !== undefined;
          }}
          getCellClassName={(p) => {
            const classes: string[] = [];
            const projectID = parseInt(p.field);
            if (!isNaN(projectID) && projectID > 0) {
              if (projectsMap[projectID]?.archived) {
                classes.push("opacity");
              }
            }
            const mission = missions.find((m) => m.musicianID === p.row.id);
            const job = seasonMercury.jobs.find(
              (j) => j.uuid === mission?.mercuryJobID
            );

            if (p.field === "memo" && job === undefined) classes.push("lock");

            return classes.join(" ");
          }}
          apiRef={datagridApi}
          getRowClassName={(row) => {
            return `musician_${row.id}`;
          }}
          sx={{ width: "calc(100vw - 40px)", maxWidth: "100%" }}
          disableColumnMenu
          disableColumnFilter
          processRowUpdate={onUpdate}
          columns={columns}
          initialState={{
            pinnedColumns: {
              right: ["id"],
              left: ["musicianID"],
            },
          }}
          rows={rows}
        />
      ) : (
        <CircularProgress />
      )}
      {/* {internalInit ? <GroupActions /> : []} */}
      <SeasonNotify />
    </Box>
  );
}

function MusicianProject({
  assignment,
  job,
}: {
  assignment?: Assignment;
  job?: MercuryMercuryJob_Entity;
}) {
  const dispatch = useDispatch();
  const stage = new Stage(assignment?.job?.currentStage);
  const utils = useSelector(seasonUtilsSelector);
  const askQuestion = useAskQuestion();
  const [getChairs] = useLazyGetChairsQuery();
  const [updateChairs] = useUpdateBatchChairsMutation();

  const onClick = assignment
    ? () => {
        dispatch(setSelectedProjectID(assignment.projectID));
        dispatch(setAssignmentIDForLookup(assignment.id));
        dispatch(setFormOpen({ isOpen: true, formID: "assignmentLookup" }));
      }
    : undefined;

  if (job?.version === 3)
    return (
      <Box
        onClick={onClick}
        sx={{
          width: "100%",
          flex: 1,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* {assignment?.mercuryStageID} */}
        {assignment ? (
          <i style={{ color: stage?.color }} className="fa-solid fa-check"></i>
        ) : (
          []
        )}
      </Box>
    );

  const color = stage?.color ?? "#bdbdbd";
  const background = alpha(color, 0.2);

  const replace = async (musicianID) => {
    const assignments = utils?.assignments?.filter(
      (a) => a.musicianID === assignment?.musicianID && a.replaceable()
    );
    let assignmentIDs = [assignment.id];
    let replaceAll = 0;
    if (assignments?.length > 1) {
      replaceAll = await askQuestion(
        "Replace for all Projects?",
        ["No, only for this Project", "Yes"],
        {
          subtitle: (
            <Typography>
              Do you want to replace the musician for all Projects accross the
              season?
            </Typography>
          ),
        }
      );
    }

    if (replaceAll) {
      assignmentIDs = assignments.reduce((a, v) => {
        a.push(v.id);
        return a;
      }, []);
    }

    const _chairs = await getChairs({
      filters: JSON.stringify([
        {
          name: "chairs.assignmentID",
          comparison: "in",
          value: assignmentIDs,
        },
      ]),
    }).unwrap();

    const chairs = _chairs.ids.reduce<RhapsodyChair_Entity[]>((a, v) => {
      a.push(_chairs.entities[v]);
      return a;
    }, []);

    for (const key in assignments) {
      if (Object.prototype.hasOwnProperty.call(assignments, key)) {
        const a = assignments[key];
        const body = [];
        const _c = chairs.filter((c) => c.assignmentID === a.id);

        _c.forEach((c) => {
          body.push({
            id: c.id,
            musicianID,
          });
        });

        await updateChairs(body).unwrap();
      }
    }

    dispatch(
      rhapsodyApi.util.invalidateTags(["seasonMusicians", "assignments"])
    );
  };

  if (!assignment || !assignment?.active) return <Box />;

  const messages = assignment?.job?.currentStage?.inspectors?.filter(
    (m) => m.title === "Message"
  );

  const firstMessage = messages?.length ? messages[0] : undefined;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        justifyContent: "start",
      }}
    >
      <Tooltip
        size="sm"
        variant="plain"
        sx={{ background: stage?.color, color: "white" }}
        title={firstMessage?.message}
      >
        <Chip
          size="sm"
          endDecorator={
            firstMessage ? <i className="fa-solid fa-comment"></i> : []
          }
          startDecorator={
            <img
              src={
                stage?.icon ??
                "https://storage.googleapis.com/rhapsody/logos/ready.png"
              }
              style={{ height: 12 }}
            />
          }
          sx={{ background, color }}
        >
          {stage?.wording() ?? "Ready"}
        </Chip>
      </Tooltip>
      {assignment?.replaceable() ? (
        <Tooltip
          variant="outlined"
          size="sm"
          arrow
          enterDelay={1000}
          enterNextDelay={1000}
          title="Replace Musician"
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ChairAssignMusician
              assignmentID={assignment?.id}
              onSelect={replace}
            >
              <Box
                sx={{
                  cursor: "pointer",
                  fontSize: 14,
                  p: 0.5,
                  color: "#6E6E6E",
                }}
              >
                <i className="fa-solid fa-rotate"></i>
              </Box>
            </ChairAssignMusician>
          </Box>
        </Tooltip>
      ) : (
        []
      )}
    </Box>
  );
}

function ProjectHeader({ projectID }) {
  const utils = useSelector(seasonUtilsSelector);
  const dispatch = useDispatch();
  const project = utils?.projectsMap[projectID];
  const workSessions = utils?.workSessions.filter(
    (w) => w.projectID === project.id
  );
  const from = project.from(workSessions);
  const to = project.to(workSessions);
  const debug = useSelector(layoutDebugSelector);
  const firstWorkSessionDate = from ? moment(from).format("l") : "";
  const lastWorkSessionDate = to ? moment(to).format("l") : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        lineHeight: 1,
        opacity: project?.archived ? 0.4 : 1,
      }}
    >
      <Link
        component={RouterLink}
        onClick={() => {
          dispatch(setBackToSeason(`${utils.tag.id}/missions`));
        }}
        color={project.archived ? "neutral" : "primary"}
        startDecorator={
          project.archived ? (
            <i className="fa-solid fa-box-archive"></i>
          ) : undefined
        }
        to={`${RouterConfig.projects}/${project?.id}/mission-control`}
        level="body2"
      >
        {debug ? <Id>{project?.id}</Id> : []}
        {project?.name}
      </Link>
      <Typography level="body4">
        {workSessions?.length} Work Sessions
      </Typography>
      {from && to ? (
        <Typography level="body5">
          {firstWorkSessionDate === lastWorkSessionDate
            ? lastWorkSessionDate
            : `${firstWorkSessionDate} - ${lastWorkSessionDate}`}
        </Typography>
      ) : (
        []
      )}
    </Box>
  );
}

function MusicianCell({
  musician,
  mission,
}: {
  musician?: Musician;
  mission: Mission_Entity;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* {mission ? <MissionCheckbox missionIDs={[mission.id]} /> : []} */}
      <MusicianCheckbox musicianIDs={[musician.id]} />
      <MusicianAvatar noBadge size={28} musician={musician} />
      <Box>
        <Typography
          level="body3"
          sx={{ color: "black", fontWeight: 600, fontSize: 12 }}
        >
          <b>{musician?.fullName()}</b>
        </Typography>
        <Typography level="body4">
          <b>{musician?.instrumentNames}</b>
        </Typography>
      </Box>
    </Box>
  );
}

export function MusicianCheckbox({
  musicianIDs,
  size,
}: {
  musicianIDs: number[];
  size?: "sm" | "md" | "lg";
}) {
  const selectedMusicianIDs = useSelector(selectedMusiciansSelector);
  const dispatch = useDispatch();

  const { checked, intermediate } = musicianIDs.reduce(
    (a, v) => {
      const index = selectedMusicianIDs?.findIndex((p) => p === v);
      if (index === -1) a.checked = false;
      if (index >= 0) a.intermediate = true;
      return a;
    },
    {
      checked: musicianIDs?.length === 0 ? false : true,
      intermediate: false,
    }
  );

  return (
    <Checkbox
      id="selecto-checkbox"
      onChange={(e) => {
        if (!e.target.checked) {
          return dispatch(unselectMusicians(musicianIDs));
        }
        dispatch(selectMusicians(musicianIDs));
      }}
      checkedIcon={
        !checked && intermediate ? (
          <i className="fa-solid fa-minus"></i>
        ) : undefined
      }
      variant={intermediate && !checked ? "soft" : undefined}
      size={size}
      checked={checked || intermediate}
    />
  );
}

function MusicianHeader() {
  const seasonUtils = useSelector(seasonUtilsSelector);

  const allMusicianIDs: number[] = seasonUtils.musicians.reduce((a, v) => {
    a.push(v.id);

    return a;
  }, []);

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <MusicianCheckbox musicianIDs={allMusicianIDs} />
      <Typography level="body2">Musicians</Typography>
    </Box>
  );
}
